"use client";

import { Info } from "lucide-react";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";
import type { Locale } from "@/lib/i18n";
import { getOwnershipCopy } from "../lib/ownership-copy";
import { scrollToCoursePricingComparison } from "../lib/scroll-course-pricing";

type Props = {
  locale: Locale;
  planDisplayName: string;
  hasActiveUpgradeTarget: boolean;
  className?: string;
};

/**
 * Owned-course pricing banner: enrolled tier + optional upgrade discovery link,
 * or final-tier / no-upgrade copy (replaces accessScope-driven “full access” alone).
 */
export function CoursePricingOwnedStatusBanner({
  locale,
  planDisplayName,
  hasActiveUpgradeTarget,
  className,
}: Props) {
  const t = useTranslations("courseDetail");
  const copy = getOwnershipCopy(locale);

  if (hasActiveUpgradeTarget) {
    return (
      <div
        role="status"
        aria-live="polite"
        className={cn(
          "mb-4 flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3",
          className,
        )}
      >
        <Info
          className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-700"
          aria-hidden
        />
        <div className="space-y-2 text-[13px] leading-snug text-emerald-700">
          <p className="font-semibold">
            {t("planOwnership.enrolledWithUpgradesTitle", { planName: planDisplayName })}
          </p>
          <button
            type="button"
            className="font-medium text-emerald-800 underline decoration-emerald-400/80 underline-offset-2 transition-colors hover:text-emerald-900"
            onClick={() => scrollToCoursePricingComparison()}
          >
            {t("planOwnership.seeUpgradeOptions")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "mb-4 flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3",
        className,
      )}
    >
      <Info
        className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-700"
        aria-hidden
      />
      <div className="space-y-1 text-[13px] leading-snug text-emerald-700">
        <p className="font-semibold">{copy.legacyBannerTitle}</p>
        <p className="text-emerald-700/90">{copy.legacyBannerBody}</p>
      </div>
    </div>
  );
}
